import React from "react";
import {
  Collapse,
  IconButton,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  Stack,
  Avatar,
  Typography,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Tag as TagIcon,
} from "@mui/icons-material";
import { CheckStatus } from "../../../../../../../utils/enums";

const renderCheckDetails = (associatedChecks) => {
  if (!associatedChecks || associatedChecks?.length === 0) {
    return (
      <Box sx={{ p: 2, backgroundColor: "#fafbfc", textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          No checks are associated with this evidence file.
        </Typography>
      </Box>
    );
  }

  const getCheckStatusConfig = (status) => {
    if (status === CheckStatus?.PASS) {
      return {
        color: "#15803d",
        bgColor: "#f0fdf4",
        borderColor: "#22c55e",
        icon: <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 16 }} />,
      };
    }
    return {
      color: "#6b7280",
      bgColor: "white",
      borderColor: "#e2e8f0",
      icon: null,
    };
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#fafbfc" }}>
      <Stack spacing={2}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                mr: 1.5,
              }}
            />
            <Typography variant="body2" fontWeight={600} color="#15803d">
              Checks Linked ({associatedChecks?.length})
            </Typography>
          </Box>
          <Stack spacing={1}>
            {associatedChecks
              ?.filter((c) => c)
              ?.map((check, idx) => {
                const statusConfig = getCheckStatusConfig(check?.status);
                return (
                  <Box
                    key={check?.active_check_id || idx}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      borderLeft: `3px solid ${statusConfig?.borderColor}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: "#f0fdf4",
                        color: "#15803d",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                      }}
                    >
                      #{idx + 1}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="#1f2937"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {check?.active_check_description}
                      </Typography>
                      <Typography variant="caption" color="#6b7280">
                        ID: {check?.active_check_id}
                      </Typography>
                    </Box>
                    {statusConfig?.icon}
                  </Box>
                );
              })}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const EvidenceListItem = ({
  fileDetail,
  index,
  currentPage,
  ITEMS_PER_PAGE,
  expandedEvidenceId,
  toggleExpand,
  handleDownloadDocument,
}) => {
  const isExpanded = expandedEvidenceId === fileDetail?.fileId;

  return (
    <React.Fragment>
      <ListItem
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #ddd",
          backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
          p: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#555",
            marginRight: 2,
            width: "20px",
          }}
        >
          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}.
        </Typography>

        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            variant="body1"
            onClick={() =>
              handleDownloadDocument(fileDetail?.path, fileDetail?.maskName)
            }
            sx={{
              fontWeight: 500,
              color: "#1976d2",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {fileDetail?.maskName}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            size="small"
            onClick={() => toggleExpand(fileDetail?.fileId)}
            sx={{
              color: "primary.main",
              backgroundColor: isExpanded ? "#e1f1ff" : "#f0f7ff",
              "&:hover": { backgroundColor: "#e1f1ff" },
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </ListItem>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {renderCheckDetails(fileDetail?.associatedChecks)}
      </Collapse>
    </React.Fragment>
  );
};

const EvidenceSection = ({
  totalItems,
  paginatedData,
  currentPage,
  totalPages,
  handlePageChange,
  expandedEvidenceId,
  toggleExpand,
  handleDownloadDocument,
  ITEMS_PER_PAGE,
  staffMember,
}) => (
  <Card
    sx={{
      borderRadius: 2,
      mb: 3,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      border: "1px solid #e2e8f0",
    }}
    variant="outlined"
  >
    <Box
      sx={{
        borderBottom: "1px solid #ddd",
        padding: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "15px",
        backgroundColor: "#f8fafc",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <TagIcon sx={{ color: "#031B89" }} />
        <Typography variant="body1" fontWeight="bold">
          Linked Evidences
        </Typography>
        <Tooltip title="Evidence files are automatically checked against compliance requirements">
          <InfoIcon fontSize="small" sx={{ color: "#666", cursor: "help" }} />
        </Tooltip>
      </Box>
    </Box>
    <CardContent sx={{ padding: "unset", paddingBottom: "unset !important" }}>
      {totalItems === 0 ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: "center", padding: "16px" }}
        >
          {`No Evidence Found Uploaded for ${staffMember?.name}`}
        </Typography>
      ) : (
        <>
          <List sx={{ padding: 0, margin: 0 }}>
            {paginatedData?.map((fileDetail, index) => (
              <EvidenceListItem
                key={fileDetail?.fileId}
                fileDetail={fileDetail}
                index={index}
                currentPage={currentPage}
                ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                expandedEvidenceId={expandedEvidenceId}
                toggleExpand={toggleExpand}
                handleDownloadDocument={handleDownloadDocument}
              />
            ))}
          </List>

          {totalPages > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              paddingTop={2}
              paddingBottom={2}
              sx={{ borderTop: "1px solid #ddd" }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

export default EvidenceSection;
